import React, { useEffect, useMemo, useRef } from 'react';
import { message, Table } from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import { get } from 'lodash';
import { ColumnsType } from 'antd/es/table';
import CustomForm, { IFieldItem, IMyFormRef } from '@/components/CustomForm';
import FixStickyTable from '@/components/FixStickyTable';
import RangeDateDynamicLimit from '@/components/RangeDateDynamicLimit';
import { EmptyHint } from '@/components/EmptyHint';
import { getAccountFlow, saveData } from '@/redux/actions/guest';
import { AccountFlow, IguestInfo } from '@/redux/reducers/guest';
import { ActionType, ACTION_TYPE_LIST, ACTION_TYPE_MAP, DATE_TIME_FORMAT } from '@/config';
import { parseFen2Yuan } from '@/utils/number';
import { filterFormParams, renderIntlLabel, uuid } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';
import RangeDateLimit from '../RangeDateLimit';
import styles from './index.module.scss';

const columns: ColumnsType<AccountFlow> = [
  {
    title: renderIntlLabel('order_selfService_detail_billType'),
    dataIndex: 'billType',
    render: (val) => ACTION_TYPE_MAP[val],
  },
  {
    title: renderIntlLabel('order_selfService_detail_serviceTime'),
    dataIndex: 'serviceTime',
    render: (val) => (val ? getTimeByUtcOffset(val, DATE_TIME_FORMAT) : ''),
  },
  {
    title: renderIntlLabel('order_selfService_detail_priorPeriodCash'),
    dataIndex: 'priorPeriodCash',
    render: (val) => parseFen2Yuan(val),
  },
  {
    title: renderIntlLabel('order_selfService_detail_optionAmount'),
    dataIndex: 'optionAmount',
    render: (val, record) => {
      if (record.billType === ActionType.Recharge || record.billType === ActionType.Return) {
        return `+${parseFen2Yuan(val)}`;
      }
      if (
        record.billType === ActionType.Consume ||
        record.billType === ActionType.Refund ||
        record.billType === ActionType.Withdrawal
      ) {
        return `-${parseFen2Yuan(val)}`;
      }
      return '';
    },
  },
  {
    title: renderIntlLabel('order_selfService_detail_currentPeriodCash'),
    dataIndex: 'currentPeriodCash',
    render: (val) => parseFen2Yuan(val),
  },
  {
    title: renderIntlLabel('order_commonCode'),
    dataIndex: 'commonCode',
    render: (val) => val || '—',
  },
  {
    title: renderIntlLabel('order_selfService_detail_source'),
    dataIndex: 'source',
    // render: (val) => (val === 'djjd' ? 'PMS' : '其他'),
  },
  {
    title: renderIntlLabel('order_selfService_detail_transactionNo'),
    dataIndex: 'orderNo',
  },
  {
    title: renderIntlLabel('order_selfService_detail_serviceTradebillNo'),
    dataIndex: 'serviceTradebillNo',
  },
];

export const transformRangeTime = (values) => {
  const params = { ...values };
  const { serviceTime } = params;
  const startStr = get(serviceTime, '[0]');
  const endStr = get(serviceTime, '[1]');
  delete params.serviceTime;
  if (startStr) {
    params.startTime = moment(startStr).valueOf();
  }
  if (endStr) {
    params.endTime = moment(endStr).valueOf();
  }
  return filterFormParams(params);
};

interface RunningAccountProps {
  guestInfo?: Partial<IguestInfo>;
  accountFlowList?: any;
  accountFlowLoading?: boolean;
  fnGetAccountFlow?: (..._) => void;
  fnSaveData?: (..._) => void;
}

const getDefaultFormValues = () => {
  return {
    serviceTime: [moment().subtract(3, 'month'), moment()],
  };
};

const RunningAccount: React.FC<RunningAccountProps> = ({
  guestInfo,
  accountFlowList,
  accountFlowLoading,
  fnGetAccountFlow,
  fnSaveData,
}) => {
  const searchRef = useRef<IMyFormRef>();

  const onReset = () => {
    const defaultFormValues = getDefaultFormValues();
    searchRef.current.setFieldsValue(defaultFormValues);
    fnGetAccountFlow({ wyuid: guestInfo?.wyuid, ...transformRangeTime(defaultFormValues) });
  };

  const handleBlur = () => {
    const values = searchRef.current.getFieldsValue();
    if (!values?.serviceTime) {
      message.warning(renderIntlLabel('guest_msg_please_select_business_time'));
      return;
    }
    fnGetAccountFlow({ wyuid: guestInfo?.wyuid, ...transformRangeTime(values) });
  };

  useEffect(() => {
    if (guestInfo?.wyuid) {
      const defaultFormValues = getDefaultFormValues();
      searchRef.current.setFieldsValue(defaultFormValues);
      fnGetAccountFlow({ wyuid: guestInfo?.wyuid, ...transformRangeTime(defaultFormValues) });
    }
  }, [guestInfo]);

  useEffect(() => {
    return () => {
      fnSaveData({ accountFlowList: [] });
    };
  }, []);

  const formAttr: Array<IFieldItem> = useMemo(() => {
    return [
      {
        label: renderIntlLabel('order_selfService_detail_billType'),
        key: 'billType',
        type: 'select',
        dataSource: ACTION_TYPE_LIST,
        componentProps: {
          style: {
            width: 240,
          },
          onBlur: handleBlur,
        },
      },
      {
        label: renderIntlLabel('order_selfService_detail_serviceTime'),
        key: 'serviceTime',
        type: 'group',
        component: (
          <RangeDateDynamicLimit
            isShowTime
            defaultRangeDate={[moment().subtract(3, 'month'), moment().add(1000, 'year')]}
            rangeCount={90}
            onBlur={handleBlur}
          />
        ),
      },
      {
        label: renderIntlLabel('order_selfService_detail_transactionNo'),
        key: 'orderNo',
        componentProps: {
          maxLength: 100,
          onBlur: handleBlur,
        },
      },
      {
        label: renderIntlLabel('order_selfService_detail_serviceTradebillNo'),
        key: 'serviceTradebillNo',
        componentProps: {
          maxLength: 100,
          onBlur: handleBlur,
        },
      },
    ];
  }, [guestInfo.wyuid]);

  return (
    <>
      <div className={styles.search}>
        <CustomForm
          ref={searchRef}
          layout="inline"
          submitType="filter"
          queryText={renderIntlLabel('order_action_query')}
          formAttr={formAttr}
          onReset={onReset}
          showBtn
          noSearch
        />

        {Array.isArray(accountFlowList) && !!accountFlowList.length ? (
          <FixStickyTable>
            <Table
              rowKey={uuid()}
              loading={accountFlowLoading}
              columns={columns}
              dataSource={accountFlowList}
              pagination={false}
              scroll={{ x: 'max-content' }}
              style={{ marginTop: '16px' }}
              sticky
            />
          </FixStickyTable>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 200 }}>
            <EmptyHint hintText={renderIntlLabel('app_no_data')} />
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.guest,
  };
};

export default connect(mapStateToProps, {
  fnGetAccountFlow: getAccountFlow,
  fnSaveData: saveData,
})(React.memo(RunningAccount));
